import { useForm } from '@inertiajs/react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toastFlash } from '@/lib/flash-toast';
import type { TimeSlot } from '@/types';
import {
  type TimeSlotFormData,
  TimeSlotFormFields,
} from './TimeSlotFormFields';
import { toDateTimeLocal, toInstant } from './timeSlotForm';

interface EditTimeSlotDialogProps {
  eventSlug: string;
  timeSlot: TimeSlot;
  onClose: () => void;
}

// Mounted only while a slot is being edited and keyed on its id, which is what the
// `form.reset(...)` inside the Next dialog's effect achieved.
export function EditTimeSlotDialog({
  eventSlug,
  timeSlot,
  onClose,
}: EditTimeSlotDialogProps) {
  const form = useForm<TimeSlotFormData>({
    title: timeSlot.title,
    slug: timeSlot.slug,
    description: timeSlot.description || '',
    startTime: toDateTimeLocal(new Date(timeSlot.startTime)),
    endTime: toDateTimeLocal(new Date(timeSlot.endTime)),
    location: timeSlot.location || '',
    color: timeSlot.color,
    target: timeSlot.target,
  });
  const { data, setData, processing } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.transform((values) => ({
      ...values,
      startTime: toInstant(values.startTime),
      endTime: toInstant(values.endTime),
    }));
    form.patch(`/admin/${eventSlug}/time-slots/${timeSlot.id}`, {
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, 'Time slot updated successfully!');
        onClose();
      },
      onError: () =>
        toast({ type: 'error', description: 'Failed to update time slot' }),
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Time Slot</DialogTitle>
          <DialogDescription>
            Update the time slot information.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <TimeSlotFormFields
            idPrefix="edit-time-slot"
            data={data}
            onChange={(patch) => setData({ ...data, ...patch })}
            errors={errors}
            processing={processing}
          />

          {errors.base && (
            <div className="rounded-md border border-red-200 bg-red-50 p-4">
              <p className="text-red-600 text-sm">{errors.base}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Updating...' : 'Update Time Slot'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
